import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSetupTotpMutation, useVerifyTotpMutation } from '@/store/api/authApi';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const schema = z.object({
  code: z.string().min(6).max(6),
});

type FormValues = z.infer<typeof schema>;

export function Setup2FAPage() {
  const [setupTotp, { data: setupData, isLoading: isSetupLoading }] = useSetupTotpMutation();
  const [verifyTotp, { isLoading: isVerifyLoading }] = useVerifyTotpMutation();
  const [message, setMessage] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    void setupTotp();
  }, [setupTotp]);

  const onSubmit = async (values: FormValues) => {
    try {
      await verifyTotp({ code: values.code }).unwrap();
      setMessage('2FA activado correctamente');
    } catch {
      setMessage('Código inválido');
    }
  };

  return (
    <Card className="mx-auto max-w-lg p-6">
      <h1 className="mb-4 text-xl font-semibold">Configurar 2FA</h1>
      {isSetupLoading && <p className="text-sm text-muted-foreground">Generando secreto...</p>}
      {setupData && (
        <div className="mb-4 space-y-2 text-sm">
          <p>Agregá esta cuenta en tu app de autenticación:</p>
          <p className="break-all rounded-md bg-muted p-2 font-mono text-xs">{setupData.otpauth}</p>
          <p>
            Secreto manual:
            {' '}
            <span className="font-mono">{setupData.secret}</span>
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="text-sm font-medium">Código de verificación</label>
          <Input inputMode="numeric" maxLength={6} {...register('code')} />
          {errors.code && <p className="mt-1 text-xs text-destructive">{errors.code.message}</p>}
        </div>
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
        <Button type="submit" disabled={isVerifyLoading}>
          Verificar y activar
        </Button>
      </form>
    </Card>
  );
}
